/** Desktop product thumbnails: page whole items, without native scrolling. */
class ThumbnailCarousel extends HTMLElement {
  first = 0;
  last = 0;

  connectedCallback() {
    this.rail = this.querySelector('.slideshow-controls__thumbnails-container');
    this.track = this.querySelector('.slideshow-controls__thumbnails');
    if (!this.rail || !this.track) return;
    this.buttons = [...this.querySelectorAll('[data-thumbnail-step]')];
    this.addEventListener('click', this.handleClick);
    this.addEventListener('load', this.update, true);
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this);
    this.observer.observe(this.track);
    this.selectionObserver = new MutationObserver(() => {
      const selected = this.items.findIndex((item) => item.getAttribute('aria-current') === 'true');
      if (selected >= 0 && (selected < this.first || selected > this.last)) this.first = selected;
      this.update();
    });
    this.selectionObserver.observe(this.track, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('load', this.update, true);
    this.observer?.disconnect();
    this.selectionObserver?.disconnect();
    this.animation?.cancel();
  }

  get items() {
    return [...this.track.querySelectorAll('.slideshow-controls__thumbnail')];
  }

  update = () => {
    if (matchMedia('(max-width: 749px)').matches || !this.hasAttribute('data-vertical-thumbnails')) {
      this.animation?.cancel();
      this.animating = false;
      this.rail.style.removeProperty('height');
      this.track.style.removeProperty('transform');
      for (const item of this.items) item.removeAttribute('data-thumbnail-clipped');
      for (const button of this.buttons) button.hidden = true;
      return;
    }
    const items = this.items;
    if (!items.length) return;
    const gap = parseFloat(getComputedStyle(this.track).rowGap) || 0;
    const heights = items.map((item) => item.getBoundingClientRect().height);
    const sum = (start, end) => heights.slice(start, end + 1).reduce((total, height) => total + height, 0) + gap * (end - start);
    const overflow = sum(0, items.length - 1) > this.clientHeight + 1;
    this.first = overflow ? Math.min(this.first, items.length - 1) : 0;
    const oneArrowCapacity = this.clientHeight - 40;
    while (this.first > 0 && sum(this.first - 1, items.length - 1) <= oneArrowCapacity) this.first--;
    const reachesEnd = this.first > 0 && sum(this.first, items.length - 1) <= oneArrowCapacity;
    const arrowSpace = overflow ? (this.first === 0 || reachesEnd ? 40 : 80) : 0;
    this.capacity = Math.max(heights[0], this.clientHeight - arrowSpace);
    this.last = this.first;
    while (this.last + 1 < items.length && sum(this.first, this.last + 1) <= this.capacity + 1) this.last++;
    const offset = heights.slice(0, this.first).reduce((total, height) => total + height, 0) + gap * this.first;
    this.rail.style.height = `${sum(this.first, this.last)}px`;
    this.track.style.transform = `translateY(${-offset}px)`;
    if (!this.animating) {
      items.forEach((item, index) => item.toggleAttribute('data-thumbnail-clipped', index < this.first || index > this.last));
    }
    for (const button of this.buttons) {
      button.hidden = !overflow;
      button.disabled = Number(button.dataset.thumbnailStep) < 0 ? this.first === 0 : this.last === items.length - 1;
    }
  };

  handleClick = (event) => {
    const button = event.target.closest('[data-thumbnail-step]');
    if (!button || button.disabled) return;
    event.stopPropagation();
    const direction = Number(button.dataset.thumbnailStep);
    const previousTransform = getComputedStyle(this.track).transform;
    const previousTop = this.rail.getBoundingClientRect().top;
    this.animation?.cancel();
    this.animating = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (direction > 0) {
      this.first = this.last + 1;
    } else {
      const items = this.items;
      const gap = parseFloat(getComputedStyle(this.track).rowGap) || 0;
      let height = 0;
      let start = this.first;
      while (start > 0) {
        const nextHeight = items[start - 1].getBoundingClientRect().height + (height ? gap : 0);
        if (height + nextHeight > this.capacity) break;
        height += nextHeight;
        start--;
      }
      this.first = start;
    }
    this.update();
    if (this.animating) {
      for (const item of this.items) item.removeAttribute('data-thumbnail-clipped');
      const transform = this.track.style.transform;
      const shift = previousTop - this.rail.getBoundingClientRect().top;
      this.animation = this.track.animate(
        [
          { transform: `${previousTransform === 'none' ? '' : previousTransform} translateY(${shift}px)` },
          { transform },
        ],
        { duration: 350, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
      const animation = this.animation;
      animation.finished.then(() => {
        if (this.animation !== animation) return;
        this.animating = false;
        this.update();
      }).catch(() => {});
    }
  };
}

if (!customElements.get('thumbnail-carousel')) customElements.define('thumbnail-carousel', ThumbnailCarousel);
